import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useConstants } from '@/contexts/ConstantsContext';
import {
  Search,
  Plus,
  Filter,
  Phone,
  Copy,
  Calendar,
  ChevronRight,
  AlertTriangle,
  X,
  RefreshCw,
  UserPlus,
  Archive,
  Clock,
  CheckSquare,
  Square,
  MessageSquare,
  ExternalLink,
  Globe,
  ChevronLeft,
  ChevronDown,
  User
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import QuickLogModal from '@/components/QuickLogModal';
import SetFollowUpModal from '@/components/SetFollowUpModal';
import CreateLeadModal from '@/components/CreateLeadModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getStageClass = (stage) => {
  const stageMap = {
    'Prospecting': 'stage-prospecting',
    'Appointment Scheduled': 'stage-appointment',
    'Qualified To Buy': 'stage-qualified',
    'Presentation Scheduled': 'stage-presentation',
    'Decision Maker Bought-In': 'stage-decision',
    'Contract Sent': 'stage-contract',
    'Closed Won': 'stage-won',
    'Closed Lost': 'stage-lost',
  };
  return stageMap[stage] || 'stage-prospecting';
};

const getIcpClass = (icp) => {
  const icpMap = {
    'Good': 'badge-good',
    'Maybe': 'badge-maybe',
    'Bad': 'badge-bad',
  };
  return icpMap[icp] || '';
};

// Helper to format website URL
const formatWebsiteUrl = (url) => {
  if (!url) return null;
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
};

// Helper to get display website
const getDisplayWebsite = (url) => {
  if (!url) return null;
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

const LeadsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOwner, isAdmin, isSetter, user } = useAuth();
  const { pipeline_stages, all_pipeline_stages, industries, sectors, niches_by_sector, country_markets, icp_fits, lead_sources, priorities, users } = useConstants();
  const listRef = useRef(null);

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // Pagination
  const pageSize = 50;
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  
  // Initialize state from URL params
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState({
    owner_id: searchParams.get('owner_id') || '',
    stage: searchParams.get('stage') || '',
    country_market: searchParams.get('country_market') || '',
    industry: searchParams.get('industry') || '',
    sector: searchParams.get('sector') || '',
    niche: searchParams.get('niche') || '',
    icp_fit: searchParams.get('icp_fit') || '',
    lead_source: searchParams.get('lead_source') || '',
    priority: searchParams.get('priority') || '',
    stale_only: searchParams.get('stale_only') === 'true',
  });
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [logModalOpen, setLogModalOpen] = useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  
  // Quick note modal
  const [quickNoteOpen, setQuickNoteOpen] = useState(false);
  const [quickNoteText, setQuickNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  
  // Bulk selection (admin only)
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkAssignToUser, setBulkAssignToUser] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Scroll position persistence
  const scrollPositionKey = 'leads_scroll_position';

  // Restore scroll position on mount
  useEffect(() => {
    const savedPosition = sessionStorage.getItem(scrollPositionKey);
    if (savedPosition && listRef.current) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedPosition));
      }, 100);
    }
    return () => {
      // Save scroll position on unmount
      sessionStorage.setItem(scrollPositionKey, window.scrollY.toString());
    };
  }, []);

  // Update URL when filters/page change
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (page > 1) params.set('page', page.toString());
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== false) params.set(key, value.toString());
    });
    setSearchParams(params, { replace: true });
  }, [filters, search, page]);

  useEffect(() => {
    fetchLeads();
  }, [filters, page]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      if (search) params.append('search', search);
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());

      const [leadsRes, countRes] = await Promise.all([
        axios.get(`${API}/leads?${params.toString()}`),
        axios.get(`${API}/leads/count?${params.toString()}`)
      ]);
      
      setLeads(leadsRes.data);
      setTotalCount(countRes.data.count);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchLeads();
  };

  const handleCopyPhone = (phone, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(phone);
    toast.success('Phone copied');
  };

  const clearFilters = () => {
    setFilters({
      owner_id: '',
      stage: '',
      country_market: '',
      industry: '',
      sector: '',
      niche: '',
      icp_fit: '',
      lead_source: '',
      priority: '',
      stale_only: false,
    });
    setSearch('');
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  const activeFiltersCount = Object.values(filters).filter(v => v && v !== false).length + (search ? 1 : 0);

  // Quick note handlers
  const openQuickNote = (lead, e) => {
    e.stopPropagation();
    setSelectedLead(lead);
    setQuickNoteText('');
    setQuickNoteOpen(true);
  };

  const handleSaveQuickNote = async () => {
    if (!quickNoteText.trim() || !selectedLead) return;
    
    setSavingNote(true);
    try {
      await axios.post(`${API}/leads/${selectedLead.id}/quick-note`, {
        note: quickNoteText.trim()
      });
      toast.success('Note added');
      setQuickNoteOpen(false);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  // Bulk selection handlers
  const toggleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(l => l.id));
    }
  };

  const openBulkAction = (action) => {
    setBulkAction(action);
    setBulkActionDialogOpen(true);
  };

  const handleBulkAction = async () => {
    if (selectedLeads.length === 0) return;

    setBulkProcessing(true);
    try {
      if (bulkAction === 'reassign') {
        if (!bulkAssignToUser) {
          toast.error('Please select a user');
          setBulkProcessing(false);
          return;
        }
        await axios.post(`${API}/leads/bulk-reassign`, {
          lead_ids: selectedLeads,
          new_owner_id: bulkAssignToUser
        });
        toast.success(`Reassigned ${selectedLeads.length} leads`);
      } else if (bulkAction === 'unassign') {
        await axios.post(`${API}/leads/bulk-reassign`, {
          lead_ids: selectedLeads,
          new_owner_id: null
        });
        toast.success(`Unassigned ${selectedLeads.length} leads`);
      } else if (bulkAction === 'archive') {
        await axios.post(`${API}/leads/bulk-archive`, {
          lead_ids: selectedLeads,
          archived: true
        });
        toast.success(`Archived ${selectedLeads.length} leads`);
      }
      
      setSelectedLeads([]);
      setBulkActionDialogOpen(false);
      setBulkAssignToUser('');
      fetchLeads();
    } catch (error) {
      toast.error('Bulk action failed');
    } finally {
      setBulkProcessing(false);
    }
  };

  // Pagination
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;

  // Navigate to lead and save scroll position
  const navigateToLead = (leadId) => {
    sessionStorage.setItem(scrollPositionKey, window.scrollY.toString());
    navigate(`/leads/${leadId}`);
  };

  // Open in new tab
  const openInNewTab = (leadId, e) => {
    e.stopPropagation();
    window.open(`/leads/${leadId}`, '_blank');
  };

  // Use all stages for admin filter, limited for setter
  const stageOptions = isAdmin ? all_pipeline_stages : pipeline_stages;

  // Get niches for selected sector
  const availableNiches = filters.sector ? (niches_by_sector[filters.sector] || []) : [];

  return (
    <div className="space-y-6" data-testid="leads-list-page" ref={listRef}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Leads</h1>
          <p className="text-muted-foreground mt-1">
            {totalCount} total leads
            {isSetter && ' (assigned to you)'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && selectedLeads.length > 0 && (
            <>
              <Button variant="outline" onClick={() => openBulkAction('reassign')} className="border-border">
                <UserPlus className="w-4 h-4 mr-2" />
                Reassign ({selectedLeads.length})
              </Button>
              <Button variant="outline" onClick={() => openBulkAction('unassign')} className="border-border">
                Unassign
              </Button>
              <Button variant="outline" onClick={() => openBulkAction('archive')} className="border-destructive text-destructive">
                <Archive className="w-4 h-4 mr-2" />
                Archive
              </Button>
            </>
          )}
          <Button onClick={() => setCreateModalOpen(true)} data-testid="create-lead-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-10 bg-secondary border-border"
            data-testid="leads-search"
          />
        </div>
        
        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="border-border" data-testid="filters-btn">
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {activeFiltersCount > 0 && (
                <Badge variant="secondary" className="ml-2">{activeFiltersCount}</Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent className="bg-card border-l border-border">
            <SheetHeader>
              <SheetTitle>Filter Leads</SheetTitle>
            </SheetHeader>
            <ScrollArea className="h-[calc(100vh-120px)] mt-6">
              <div className="space-y-4 pr-4">
                {isAdmin && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Owner</label>
                    <Select value={filters.owner_id || "all"} onValueChange={(v) => setFilters({...filters, owner_id: v === "all" ? "" : v})}>
                      <SelectTrigger className="bg-secondary border-border">
                        <SelectValue placeholder="All owners" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All owners</SelectItem>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium">Stage</label>
                  <Select value={filters.stage || "all"} onValueChange={(v) => setFilters({...filters, stage: v === "all" ? "" : v})}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue placeholder="All stages" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All stages</SelectItem>
                      {stageOptions.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Market</label>
                  <Select value={filters.country_market || "all"} onValueChange={(v) => setFilters({...filters, country_market: v === "all" ? "" : v})}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue placeholder="All markets" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All markets</SelectItem>
                      {country_markets.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Sector</label>
                  <Select value={filters.sector || "all"} onValueChange={(v) => setFilters({...filters, sector: v === "all" ? "" : v, niche: ""})}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue placeholder="All sectors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All sectors</SelectItem>
                      {sectors.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {filters.sector && availableNiches.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Niche</label>
                    <Select value={filters.niche || "all"} onValueChange={(v) => setFilters({...filters, niche: v === "all" ? "" : v})}>
                      <SelectTrigger className="bg-secondary border-border">
                        <SelectValue placeholder="All niches" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All niches</SelectItem>
                        {availableNiches.map(n => (
                          <SelectItem key={n} value={n}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium">Industry</label>
                  <Select value={filters.industry || "all"} onValueChange={(v) => setFilters({...filters, industry: v === "all" ? "" : v})}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue placeholder="All industries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All industries</SelectItem>
                      {industries.map(i => (
                        <SelectItem key={i} value={i}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">ICP Fit</label>
                  <Select value={filters.icp_fit || "all"} onValueChange={(v) => setFilters({...filters, icp_fit: v === "all" ? "" : v})}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue placeholder="All fits" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All fits</SelectItem>
                      {icp_fits.map(f => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Lead Source</label>
                  <Select value={filters.lead_source || "all"} onValueChange={(v) => setFilters({...filters, lead_source: v === "all" ? "" : v})}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue placeholder="All sources" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All sources</SelectItem>
                      {lead_sources.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Priority</label>
                  <Select value={filters.priority || "all"} onValueChange={(v) => setFilters({...filters, priority: v === "all" ? "" : v})}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue placeholder="All priorities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All priorities</SelectItem>
                      {priorities.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  variant="outline"
                  className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  onClick={() => setFilters({...filters, stale_only: !filters.stale_only})}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {filters.stale_only ? 'Showing Stale Only' : 'Show Stale Leads'}
                </Button>

                {activeFiltersCount > 0 && (
                  <Button variant="ghost" className="w-full" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-2" />
                    Clear all filters
                  </Button>
                )}
              </div>
            </ScrollArea>
          </SheetContent>
        </Sheet>

        <Button variant="outline" onClick={() => { setPage(1); fetchLeads(); }} className="border-border" data-testid="refresh-leads">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Bulk Select Header (admin only) */}
      {isAdmin && leads.length > 0 && (
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={toggleSelectAll}
            className="text-muted-foreground"
          >
            {selectedLeads.length === leads.length ? (
              <><CheckSquare className="w-4 h-4 mr-2" /> Deselect All</>
            ) : (
              <><Square className="w-4 h-4 mr-2" /> Select All</>
            )}
          </Button>
          {selectedLeads.length > 0 && (
            <span>{selectedLeads.length} selected</span>
          )}
        </div>
      )}

      {/* Leads List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : leads.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <p className="text-muted-foreground">No leads found</p>
            <Button className="mt-4" onClick={() => setCreateModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create your first lead
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {leads.map((lead, index) => (
            <Card 
              key={lead.id} 
              className={`lead-card bg-card border-border cursor-pointer hover:border-primary/30 ${
                selectedLeads.includes(lead.id) ? 'border-primary bg-primary/5' : ''
              }`}
              data-testid={`lead-card-${lead.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  {/* Row number */}
                  <div className="flex-shrink-0 w-8 text-center">
                    <span className="text-sm text-muted-foreground font-mono">
                      #{startIndex + index + 1}
                    </span>
                  </div>

                  {/* Checkbox for admin */}
                  {isAdmin && (
                    <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                      <Checkbox 
                        checked={selectedLeads.includes(lead.id)}
                        onCheckedChange={() => toggleSelectLead(lead.id)}
                      />
                    </div>
                  )}
                  
                  <div 
                    className="flex-1 min-w-0"
                    onClick={() => navigateToLead(lead.id)}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{lead.full_name}</h3>
                      {lead.stale_lead && (
                        <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Stale
                        </Badge>
                      )}
                      {lead.do_not_call && (
                        <Badge variant="destructive" className="text-xs">DNC</Badge>
                      )}
                      {lead.local_time && (
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${lead.outside_call_hours ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'text-muted-foreground'}`}
                        >
                          <Clock className="w-3 h-3 mr-1" />
                          {lead.local_time}
                          {lead.outside_call_hours && ' ⚠️'}
                        </Badge>
                      )}
                      {/* DM Reached Badge (Admin only) */}
                      {isAdmin && lead.dm_reached && (
                        <Badge 
                          variant="outline" 
                          className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-xs"
                          title={lead.last_dm_reached_at ? `Last DM: ${new Date(lead.last_dm_reached_at).toLocaleDateString()}` : ''}
                        >
                          <User className="w-3 h-3 mr-1" />
                          DM Reached
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{lead.company_name}</p>

                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge variant="outline" className={`text-xs ${getStageClass(lead.stage)}`}>
                        {lead.stage}
                      </Badge>
                      <Badge variant="outline" className={`text-xs ${lead.country_market === 'UK' ? 'badge-uk' : 'badge-usa'}`}>
                        {lead.country_market}
                      </Badge>
                      <Badge variant="outline" className={`text-xs ${getIcpClass(lead.icp_fit)}`}>
                        {lead.icp_fit}
                      </Badge>
                      {lead.sector && lead.sector !== 'Other' && (
                        <span className="text-xs text-muted-foreground">{lead.sector}</span>
                      )}
                      {lead.niche && lead.niche !== 'Other' && (
                        <span className="text-xs text-primary/70">• {lead.niche}</span>
                      )}
                    </div>

                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                      <span className="font-mono">{lead.phone}</span>
                      {/* Call cadence indicator */}
                      {lead.call_attempt_number > 0 && (
                        <span className={`font-medium ${lead.call_attempt_number >= 5 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                          Attempt {lead.call_attempt_number}/6
                        </span>
                      )}
                      {lead.recommended_action && lead.call_attempt_number > 0 && (
                        <span className="text-primary/70 italic">{lead.recommended_action}</span>
                      )}
                      <span>{lead.owner_name || 'Unassigned'}</span>
                      {/* Clickable website */}
                      {lead.website && (
                        <a
                          href={formatWebsiteUrl(lead.website)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <Globe className="w-3 h-3" />
                          {getDisplayWebsite(lead.website)}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    {/* Appointment outcome badge */}
                    {lead.stage === 'Appointment Scheduled' && lead.appointment_status && (
                      <div className="mt-2">
                        <Badge variant="outline" className={`text-xs ${
                          lead.appointment_status === 'Attended' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                          lead.appointment_status === 'No show' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                          lead.appointment_status === 'Rescheduled' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' :
                          lead.appointment_status === 'Cancelled' ? 'bg-gray-500/10 text-gray-400 border-gray-500/30' :
                          'text-muted-foreground'
                        }`}>
                          Appt: {lead.appointment_status}
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setSelectedLead(lead); setLogModalOpen(true); }}
                      title="Log Call"
                    >
                      <Phone className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-border"
                      onClick={(e) => handleCopyPhone(lead.phone, e)}
                      title="Copy Phone"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-border"
                      onClick={(e) => openQuickNote(lead, e)}
                      title="Quick Note"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-border"
                      onClick={(e) => { e.stopPropagation(); setSelectedLead(lead); setFollowUpModalOpen(true); }}
                      title="Set Follow Up"
                    >
                      <Calendar className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-border"
                      onClick={(e) => openInNewTab(lead.id, e)}
                      title="Open in New Tab"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4">
          <p className="text-sm text-muted-foreground">
            Showing {startIndex + 1}-{Math.min(startIndex + pageSize, totalCount)} of {totalCount}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="border-border"
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="border-border"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={page === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPage(pageNum)}
                    className={page === pageNum ? "" : "border-border"}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="border-border"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="border-border"
            >
              Last
            </Button>
          </div>
        </div>
      )}

      {/* Quick Note Dialog */}
      <Dialog open={quickNoteOpen} onOpenChange={setQuickNoteOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Quick Note for {selectedLead?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Enter your note..."
              value={quickNoteText}
              onChange={(e) => setQuickNoteText(e.target.value)}
              className="bg-secondary border-border min-h-[100px]"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickNoteOpen(false)} className="border-border">
              Cancel
            </Button>
            <Button onClick={handleSaveQuickNote} disabled={savingNote || !quickNoteText.trim()}>
              {savingNote ? 'Saving...' : 'Save Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>
              {bulkAction === 'reassign' && `Reassign ${selectedLeads.length} Leads`}
              {bulkAction === 'unassign' && `Unassign ${selectedLeads.length} Leads`}
              {bulkAction === 'archive' && `Archive ${selectedLeads.length} Leads`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {bulkAction === 'reassign' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Assign To</label>
                <Select value={bulkAssignToUser} onValueChange={setBulkAssignToUser}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {bulkAction === 'unassign' && (
              <p className="text-muted-foreground">
                This will move {selectedLeads.length} leads to the Unassigned queue.
              </p>
            )}
            {bulkAction === 'archive' && (
              <p className="text-muted-foreground">
                This will archive {selectedLeads.length} leads. They can be restored later.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkActionDialogOpen(false)} className="border-border">
              Cancel
            </Button>
            <Button 
              onClick={handleBulkAction} 
              disabled={bulkProcessing || (bulkAction === 'reassign' && !bulkAssignToUser)}
              variant={bulkAction === 'archive' ? 'destructive' : 'default'}
            >
              {bulkProcessing ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuickLogModal
        open={logModalOpen}
        onOpenChange={setLogModalOpen}
        leadId={selectedLead?.id}
        lead={selectedLead}
        onSuccess={fetchLeads}
      />

      <SetFollowUpModal
        open={followUpModalOpen}
        onOpenChange={setFollowUpModalOpen}
        lead={selectedLead}
        onSuccess={fetchLeads}
      />

      <CreateLeadModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSuccess={fetchLeads}
      />
    </div>
  );
};

export default LeadsList;
